import { useState, useCallback } from 'react'
import { Button, CircularProgress } from '@mui/material'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  alignmentsAtom,
  currentMappingSetAtom,
  exportedSpreadsheetIdAtom,
  lessonAssociationsAtom,
  treeStructureResultAtom,
} from './atoms'
import {
  LessonAssociation,
  LessonCentricData,
  StandardAlignmentResponseType,
} from 'domains/scope-and-sequence/types'
import {
  createSpreadsheet,
  SheetTabInput,
  BatchUpdateRequest,
} from 'infrastructure/googleDrive'
import { googleUserAtom } from 'infrastructure/googleDrive'
import { nodePathString } from 'components/contentService/NodePathCell'
import { useTreeSourceMode } from './useScopeAndSequenceUrlParams'

export const EXPORT_HEADERS = [
  'Original Node Path',
  'Lesson Name',
  'Standards',
  'Aligned Standards',
  'Suggested Units',
  'Target Unit',
  'Target Section',
  'Target Lesson Number',
  'tree_id',
  'version_id',
  'node_id',
]

export const CROSSWALK_METADATA_LABEL = 'Crosswalk Mapping Set ID'

const UNIT_REFERENCE_HEADERS = ['Unit #', 'Unit Name']

export type ExportRow = {
  nodePath: string
  lessonName: string
  standards: string
  alignedStandards: string
  suggestedUnits: string
  targetUnit: string
  targetSection: string
  targetLessonNumber: string
  treeId: string
  versionId: string
  nodeId: string
}

export function sortExportRows(rows: ExportRow[]): ExportRow[] {
  return [...rows].sort(
    (a, b) =>
      a.nodePath.localeCompare(b.nodePath) ||
      a.lessonName.localeCompare(b.lessonName),
  )
}

function buildAlignedStandardsMap(
  alignments: StandardAlignmentResponseType[],
): Map<string, string[]> {
  const map = new Map<string, string[]>()
  for (const a of alignments) {
    const target = a.target_standard_code.replace('CCSS.Math.Content.', '')
    if (!map.has(target)) map.set(target, [])
    map.get(target)!.push(a.source_standard_code)
  }
  return map
}

function convertToExportRows(
  data: LessonCentricData,
  lessonAssociations: Record<string, LessonAssociation>,
  alignments: StandardAlignmentResponseType[],
): ExportRow[] {
  const alignedMap = buildAlignedStandardsMap(alignments)

  return data.lessons.map((lesson) => {
    const association = lessonAssociations[lesson.key]

    const alignedStandards = [
      ...new Set(lesson.standards.flatMap((std) => alignedMap.get(std) ?? [])),
    ]

    return {
      nodePath: nodePathString(lesson.node_path),
      lessonName: lesson.name,
      standards: lesson.standards.join(', '),
      alignedStandards: alignedStandards.join(', '),
      suggestedUnits: lesson.suggestedUnits.join(', '),
      targetUnit:
        association?.targetUnit != null ? String(association.targetUnit) : '',
      targetSection: association?.targetSection ?? '',
      targetLessonNumber:
        association?.targetLessonNumber != null
          ? String(association.targetLessonNumber)
          : '',
      treeId: lesson.tree_id,
      versionId: lesson.version_id,
      nodeId: lesson.node_id,
    }
  })
}

function buildMetadataRows(
  crosswalkMappingSetId: number | undefined,
): string[][] {
  const rows: string[][] = []
  if (crosswalkMappingSetId != null) {
    rows.push([CROSSWALK_METADATA_LABEL, String(crosswalkMappingSetId)])
  }
  return rows
}

function exportRowsToSheetData(
  rows: ExportRow[],
  metadataRows: string[][],
): string[][] {
  return [
    ...metadataRows,
    EXPORT_HEADERS,
    ...rows.map((row) => [
      row.nodePath,
      row.lessonName,
      row.standards,
      row.alignedStandards,
      row.suggestedUnits,
      row.targetUnit,
      row.targetSection,
      row.targetLessonNumber,
      row.treeId,
      row.versionId,
      row.nodeId,
    ]),
  ]
}

function createUnitReferenceData(
  units: LessonCentricData['units'],
): string[][] {
  return [
    UNIT_REFERENCE_HEADERS,
    ...units.map((unit) => [String(unit.number), unit.name]),
  ]
}

async function applySpreadsheetFormatting(
  accessToken: string,
  spreadsheetId: string,
  mainSheetId: number,
  units: LessonCentricData['units'],
  rowCount: number,
  metadataRowCount: number,
): Promise<void> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  // Build unit options for dropdown from actual unit numbers, plus EXCLUDE
  const unitOptions = units.map((u) => ({
    userEnteredValue: String(u.number),
  }))
  unitOptions.push({ userEnteredValue: 'EXCLUDE' })

  const headerRow = metadataRowCount
  const dataStartRow = headerRow + 1
  const dataEndRow = dataStartRow + rowCount

  const requests: BatchUpdateRequest[] = [
    // Header row formatting - blue background, white bold text
    {
      repeatCell: {
        range: {
          sheetId: mainSheetId,
          startRowIndex: headerRow,
          endRowIndex: headerRow + 1,
          startColumnIndex: 0,
          endColumnIndex: EXPORT_HEADERS.length,
        },
        cell: {
          userEnteredFormat: {
            backgroundColor: { red: 0.26, green: 0.52, blue: 0.96 }, // Google blue
            textFormat: {
              bold: true,
              foregroundColor: { red: 1, green: 1, blue: 1 },
            },
          },
        },
        fields:
          'userEnteredFormat.backgroundColor,userEnteredFormat.textFormat',
      },
    },
    // Read-only columns (A-E, indices 0-4) - light grey background
    {
      repeatCell: {
        range: {
          sheetId: mainSheetId,
          startRowIndex: dataStartRow,
          endRowIndex: dataEndRow,
          startColumnIndex: 0,
          endColumnIndex: 5,
        },
        cell: {
          userEnteredFormat: {
            backgroundColor: { red: 0.95, green: 0.95, blue: 0.95 },
          },
        },
        fields: 'userEnteredFormat.backgroundColor',
      },
    },
    // Hide identifier columns (I-K, indices 8-10)
    {
      updateDimensionProperties: {
        range: {
          sheetId: mainSheetId,
          dimension: 'COLUMNS',
          startIndex: 8,
          endIndex: 11,
        },
        properties: {
          hiddenByUser: true,
        },
        fields: 'hiddenByUser',
      },
    },
    // Data validation for Target Unit column (F, index 5)
    {
      setDataValidation: {
        range: {
          sheetId: mainSheetId,
          startRowIndex: dataStartRow,
          endRowIndex: dataEndRow,
          startColumnIndex: 5,
          endColumnIndex: 6,
        },
        rule: {
          condition: {
            type: 'ONE_OF_LIST',
            values: unitOptions,
          },
          showCustomUi: true,
          strict: false, // Allow other values too
        },
      },
    },
  ]

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ requests }),
  })

  if (!response.ok) {
    const data = await response.json()
    throw new Error(
      `Error formatting spreadsheet: ${data.error?.message || 'Unknown error'}`,
    )
  }
}

export function ExportToGoogleSheetsButton() {
  const guser = useAtomValue(googleUserAtom)
  const treeStructureResult = useAtomValue(treeStructureResultAtom)
  const lessonAssociations = useAtomValue(lessonAssociationsAtom)
  const currentMappingSet = useAtomValue(currentMappingSetAtom)
  const alignments = useAtomValue(alignmentsAtom)
  const setExportedSpreadsheetId = useSetAtom(exportedSpreadsheetIdAtom)
  const { switchToSpreadsheet } = useTreeSourceMode()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [spreadsheetUrl, setSpreadsheetUrl] = useState<string | null>(null)

  const handleExport = useCallback(async () => {
    if (!guser.credential?.accessToken || !treeStructureResult) return

    setLoading(true)
    setError(null)

    try {
      const exportRows = convertToExportRows(
        treeStructureResult,
        lessonAssociations,
        alignments.result,
      )
      const sortedRows = sortExportRows(exportRows)
      const metadataRows = buildMetadataRows(currentMappingSet?.id)
      const sheetData = exportRowsToSheetData(sortedRows, metadataRows)
      const unitReferenceData = createUnitReferenceData(
        treeStructureResult.units,
      )

      // Create two tabs: main lesson data and unit reference
      // Use explicit sheetIds so we can reference them in batchUpdate
      const MAIN_SHEET_ID = 0
      const frozenRows = metadataRows.length + 1 // metadata + header
      const tabs: SheetTabInput[] = [
        {
          sheetId: MAIN_SHEET_ID,
          title: `Grade ${treeStructureResult.grade} Lessons`,
          data: sheetData,
          frozenRowCount: frozenRows,
        },
        {
          sheetId: 1,
          title: 'Unit Reference',
          data: unitReferenceData,
          frozenRowCount: 1,
        },
      ]

      const spreadsheetTitle = `S&S Export - Grade ${treeStructureResult.grade}`
      const id = await createSpreadsheet(
        guser.credential.accessToken,
        spreadsheetTitle,
        tabs,
      )

      // Apply formatting: header styling, grey read-only columns, hidden ID columns, data validation
      await applySpreadsheetFormatting(
        guser.credential.accessToken,
        id,
        MAIN_SHEET_ID,
        treeStructureResult.units,
        exportRows.length,
        metadataRows.length,
      )

      setExportedSpreadsheetId(id)
      setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${id}/edit`)

      // Switch to spreadsheet workflow mode
      switchToSpreadsheet(id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export')
    } finally {
      setLoading(false)
    }
  }, [
    guser,
    treeStructureResult,
    lessonAssociations,
    currentMappingSet,
    alignments,
    setExportedSpreadsheetId,
    switchToSpreadsheet,
  ])

  if (!treeStructureResult) {
    return null
  }

  return (
    <>
      <Button
        variant="contained"
        onClick={handleExport}
        disabled={loading || !guser.credential?.accessToken}
        startIcon={loading ? <CircularProgress size={16} /> : null}
      >
        {loading ? 'Exporting...' : 'Export to Google Sheets'}
      </Button>
      {error && (
        <span style={{ color: 'red', marginLeft: 8, fontSize: '0.875rem' }}>
          {error}
        </span>
      )}
      {spreadsheetUrl && (
        <a
          href={spreadsheetUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: 8, fontSize: '0.875rem' }}
        >
          Open Spreadsheet
        </a>
      )}
    </>
  )
}
